/* eslint-disable react/prop-types */
import { useState, useRef } from 'react';
import styled from '@emotion/styled';
import PlusCourseCard from '@comp/PlusCourseCard/PlusCourseCard';
import InfiniteList from '@comp/InfiniteList/InfiniteList';
import Dialog from '@comp/Dialog/Dialog';
import FreeCourseCard from '@comp/FreeCourseCard/FreeCourseCard';
import EmptyState from '@comp/EmptyState/EmptyState';
import { fetchSeeAllTypeFeed } from '@actions/specialClasses';
import { useDispatchToStore } from '@hooks/reduxHooks';
import { toggleEnrolledCourse, toggleBookmarkItem } from '@actions/courses';
import useWindowSize from '@hooks/useWindowSize';
import { useSnackbar } from '@stores/snackbar';
import { breakpoints, Spacings } from '@styles/index';
import { getCourseEnrollMessage, seeAllTypes } from '@constants/index';
import useRefreshDataOnGloUpdate from '@hooks/useRefreshDataOnGloUpdate';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import useIsRecordedGoal from '@hooks/useIsRecordedGoal';

const PlusCard = styled(PlusCourseCard)`
  margin-top: ${Spacings.SPACING_6B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin: 0 0 ${Spacings.SPACING_6B};
  }
`;

const ClassFeedBlock = ({
  goalUID,
  classFeedBlock,
  goalkey,
  blockId,
  defaultBlockURL,
  seeAllType,
  blockType,
  dropdownOption,
  customLoadMore
}) => {
  const { isMobile } = useWindowSize();
  const dispatch = useDispatchToStore();
  const selectedGoalUID = useSelectedGoalUID();
  const [unEnrollModalOpen, setUnEnrollModalOpen] = useState(false);
  const gridColumn = isMobile ? 'span 11' : 'span 6';
  const message =
    'This course will be removed from your schedule and you will stop receiving updates related to this course';
  const courseRef = useRef('');
  const unEnrollEventProps = useRef();
  const { open: showSnackbar } = useSnackbar();
  const isRecordedGoal = useIsRecordedGoal(goalUID);
  const showAsLesson = isRecordedGoal && blockType === 'live_now';

  const toggleEnroll =
    (classData) => async (courseUid, isEnrolled, eventProps) => {
      if (isEnrolled) {
        courseRef.current = courseUid;
        unEnrollEventProps.current = eventProps;
        setUnEnrollModalOpen(true);
      } else {
        const { uid, name } = classData?.goal || {};
        await dispatch(toggleEnrolledCourse(courseUid, !isEnrolled));
        showSnackbar({
          message: getCourseEnrollMessage({
            type: 'Course',
            goalUID: uid,
            selectedGoalUID,
            goalName: name,
            isRecordedGoal
          }),
          type: 'success'
        });
      }
    };

  const acceptOnClick = () => {
    dispatch(toggleEnrolledCourse(courseRef.current, false));
    setUnEnrollModalOpen(false);
  };

  const onCloseModal = () => setUnEnrollModalOpen(false);
  const handleSaveUnSave = (courseUID, isBookmarked) => {
    dispatch(toggleBookmarkItem(courseUID, 4, isBookmarked));
  };

  const renderEachClass = (classData) => {
    const { forPlus, isSpecial } = classData;
    if (forPlus || isSpecial)
      return (
        <PlusCard
          key={classData.uid}
          course={{ ...classData, showAsLesson }}
          timeTagShow={false}
          toggleEnroll={toggleEnroll(classData)}
          dataAnalytics="goal"
          gridColumn={gridColumn}
        />
      );

    return (
      <FreeCourseCard
        key={classData.uid}
        course={classData}
        onClickSave={handleSaveUnSave}
        dataAnalytics="goal"
        gridColumn={gridColumn}
      />
    );
  };

  const loadMoreClasses = (resetWithoutEmpty, params) => {
    if (customLoadMore) {
      return customLoadMore();
    }
    return dispatch(
      fetchSeeAllTypeFeed(
        goalUID,
        dropdownOption !== '' ? `${blockType}-${dropdownOption}` : blockType,
        defaultBlockURL,
        blockId,
        seeAllType,
        true,
        resetWithoutEmpty,
        params
      )
    );
  };

  useRefreshDataOnGloUpdate(() => {
    if (seeAllType !== seeAllTypes.FREE_COURSES) {
      loadMoreClasses(true, `timestamp=${new Date().getTime()}`);
    }
  });

  if (classFeedBlock[goalkey]?.results) {
    return (
      <>
        <Dialog
          onClose={onCloseModal}
          dialogState={unEnrollModalOpen}
          heading="Are you sure you want to unenroll?"
          message={message}
          acceptLabel="Unenroll"
          rejectLabel="Cancel"
          acceptOnClick={acceptOnClick}
          rejectOnClick={onCloseModal}
        />
        <InfiniteList
          loadMore={loadMoreClasses}
          _renderItem={renderEachClass}
          data={classFeedBlock[goalkey]}
          loaderType="course"
          gridColumn="span 18"
        />
        {!classFeedBlock[goalkey]?.isFetching &&
          classFeedBlock[goalkey]?.results.length === 0 && (
            <EmptyState
              title="No classes found"
              description="There are no classes here. Try changing the filters"
            />
          )}
      </>
    );
  }
  return (
    <EmptyState
      title="No classes found"
      description="There are no classes here. Try changing the filters"
    />
  );
};

export default ClassFeedBlock;
